import {
  Users,
  MapPin,
  Monitor,
  DollarSign,
  TrendingUp,
  AlertCircle,
  ArrowUpRight,
  Clock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDashboardStats, useRecentClients, useRecentBillingEvents } from '@/hooks/useDashboard';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentClients } = useRecentClients();
  const { data: recentEvents } = useRecentBillingEvents();

  const statCards = [
    {
      name: 'Total MRR',
      value: formatCurrency(stats?.total_mrr || 0),
      icon: DollarSign,
      color: 'text-green-600 bg-green-100',
      change: '+12%',
    },
    {
      name: 'Active Clients',
      value: stats?.active_clients || 0,
      icon: Users,
      color: 'text-blue-600 bg-blue-100',
      subtitle: `${stats?.trial_clients || 0} in trial`,
    },
    {
      name: 'Total Locations',
      value: stats?.total_locations || 0,
      icon: MapPin,
      color: 'text-purple-600 bg-purple-100',
    },
    {
      name: 'Total Registers',
      value: stats?.total_registers || 0,
      icon: Monitor,
      color: 'text-orange-600 bg-orange-100',
    },
  ];

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'license_added':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'payment_failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      trial: 'bg-blue-100 text-blue-700',
      trialing: 'bg-blue-100 text-blue-700',
      suspended: 'bg-red-100 text-red-700',
      past_due: 'bg-yellow-100 text-yellow-700',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || 'bg-gray-100 text-gray-700')}>
        {status}
      </span>
    );
  };

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Overview of all BlazePOS client organizations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
          >
            <div className="flex items-center justify-between">
              <div className={cn('p-2 rounded-lg', stat.color)}>
                <stat.icon className="h-5 w-5" />
              </div>
              {stat.change && (
                <span className="text-xs font-medium text-green-600 flex items-center gap-0.5">
                  {stat.change}
                  <ArrowUpRight className="h-3 w-3" />
                </span>
              )}
            </div>
            <div className="mt-3">
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.name}</p>
              {stat.subtitle && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stat.subtitle}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Clients */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">Recent Clients</h2>
            <Link
              to="/clients"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
            {recentClients?.map((client: any) => (
              <Link
                key={client.id}
                to={`/clients/${client.slug}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{client.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {client.slug}.blazepos.app
                  </p>
                </div>
                <div className="text-right">
                  {getStatusBadge(client.subscription_status || 'trialing')}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {client.subscription_plan || 'No plan'}
                  </p>
                </div>
              </Link>
            ))}
            {(!recentClients || recentClients.length === 0) && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No clients yet
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
            {recentEvents?.map((event: any) => (
              <div key={event.id} className="flex items-start gap-3 p-4">
                <div className="mt-0.5">{getEventIcon(event.event_type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white">
                    {event.event_type.replace(/_/g, ' ')}
                    {event.resource_type && ` (${event.resource_type})`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(event.created_at)}
                  </p>
                </div>
              </div>
            ))}
            {(!recentEvents || recentEvents.length === 0) && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


